import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'

/**
 * MentorProjects — assigned projects list, or a single project's participants
 * and authored stage structure when :projectId is present.
 */
export default function MentorProjects() {
  const { projectId } = useParams()
  const [projects, setProjects] = useState(null)
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    setError(null)
    if (projectId) {
      setDetail(null)
      api.get(`/project-lab/mentor/projects/${projectId}`)
        .then((r) => setDetail(r.data))
        .catch((e) => {
          setError(e.response?.data?.error || 'Failed to load project')
          setDetail(null)
        })
    } else {
      setProjects(null)
      api.get('/project-lab/mentor/projects')
        .then((r) => setProjects(r.data.projects))
        .catch(() => setProjects([]))
    }
  }, [projectId])

  if (projectId) {
    if (error) {
      return (
        <div className="max-w-3xl space-y-4">
          <Link to="/mentor/projects" className="text-xs text-cinema-gold hover:underline">← My Projects</Link>
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )
    }
    if (!detail) return <div className="flex justify-center py-16"><LoadingSpinner /></div>
    const { project, stages, participants } = detail
    return (
      <div className="max-w-4xl space-y-10">
        <div className="space-y-2">
          <Link to="/mentor/projects" className="text-xs text-cinema-gold hover:underline">← My Projects</Link>
          <h1 className="font-display text-2xl text-white">{project.title}</h1>
          <p className="text-xs text-cinema-muted uppercase tracking-wider">
            {project.discipline} · {project.level} · {project.mode}
          </p>
          {project.short_description && (
            <p className="text-sm text-cinema-soft max-w-2xl">{project.short_description}</p>
          )}
        </div>

        <section>
          <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
            Production Stages ({stages.length})
          </h2>
          <div className="divide-y divide-white/5">
            {stages.map((s) => (
              <div key={s.stage_number} className="flex items-baseline gap-4 py-3">
                <span className="text-xs font-mono text-cinema-muted w-8 shrink-0">
                  {String(s.stage_number).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-white">{s.title}</p>
                  {s.description && <p className="text-xs text-cinema-muted mt-0.5 truncate">{s.description}</p>}
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-cinema-muted shrink-0">
                  {s.completion_mode}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
            My Participants on this Project
          </h2>
          {participants.length === 0 ? (
            <p className="text-sm text-cinema-muted py-6">No participants assigned to you on this project.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {participants.map((pt) => (
                <Link
                  key={pt.enrollment_id}
                  to={`/mentor/enrollments/${pt.enrollment_id}`}
                  className="flex items-center justify-between gap-4 py-3.5 group"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate group-hover:text-cinema-gold transition-colors">{pt.student_first_name} {pt.student_last_name}</p>
                    <p className="text-xs text-cinema-muted truncate">{pt.student_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-cinema-soft">{pt.current_stage_title || `Stage ${pt.current_stage_index}`}</p>
                    <p className="text-[10px] uppercase tracking-wider text-cinema-muted">
                      {pt.current_stage_status || pt.enrollment_status}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    )
  }

  if (projects === null) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="font-display text-2xl text-white">My Projects</h1>
        <p className="text-xs text-cinema-muted mt-1">Project Lab productions assigned to you.</p>
      </header>
      {projects.length === 0 ? (
        <p className="text-sm text-cinema-muted py-6 border-t border-white/10">
          No projects assigned yet.
        </p>
      ) : (
        <div className="divide-y divide-white/5 border-t border-white/10">
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/mentor/projects/${p.id}`}
              className="flex items-center justify-between gap-4 py-4 group"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-white group-hover:text-cinema-gold transition-colors truncate">
                  {p.title}
                </p>
                <p className="text-xs text-cinema-muted mt-0.5">{p.discipline || 'Project Lab'} · {p.status}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm text-white font-mono">{p.active_participants}</p>
                <p className="text-[10px] uppercase tracking-wider text-cinema-muted">participants</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
